import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Carousel } from "react-bootstrap";
import { formatToUSDate, calculateCountdown } from "../../utils/dateFormat";
import { FaRegClock } from "react-icons/fa";
import { HiOutlineLocationMarker } from "react-icons/hi";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import DefaultEventImage from "../../assets/images/event/DefaultEventPhoto.svg";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { formatEventDate } from "../../utils/formatEventDate";

const IMAGE_BASEURL = process.env.REACT_APP_IMAGE_BASEURL || "";

export default function EventStrip({
  upcomingEvents,
  secondaryEvents,
  loading,
}) {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.USER_DATA);

  const parseDate = (value) => {
    if (!value || typeof value !== "string") return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const withinNext30Days = (date) => {
    if (!date) return false;
    const now = new Date();
    const future = new Date();
    future.setDate(future.getDate() + 30);
    return date >= now && date <= future;
  };

  // Filter primary upcoming events to within next 30 days
  const primaryWindowed = useMemo(() => {
    if (!upcomingEvents?.length) return [];
    return upcomingEvents.filter((ev) =>
      withinNext30Days(parseDate(ev?.start_date))
    );
  }, [upcomingEvents]);

  // Prefer primary (within 30 days); otherwise fallback to up to 2 secondary events
  const displayEvents = useMemo(() => {
    if (primaryWindowed.length > 0) return primaryWindowed;
    if (secondaryEvents?.length) return secondaryEvents.slice(0, 2);
    return [];
  }, [primaryWindowed, secondaryEvents]);

  // Countdown per event (only if valid start date)
  const [countdowns, setCountdowns] = useState({});

  useEffect(() => {
    if (!displayEvents?.length) {
      setCountdowns({});
      return undefined;
    }

    const update = () => {
      const next = {};
      displayEvents.forEach((ev, idx) => {
        const key = ev?._id || idx;
        const start = parseDate(ev?.start_date);
        if (!start) return;
        const { days, hours, minutes, seconds } = calculateCountdown(start);
        next[key] = { days, hours, mins: minutes, secs: seconds };
      });
      setCountdowns(next);
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [displayEvents]);

  return (
    <>
      {displayEvents?.length > 0 && (
        <section className="event-strip my-5">
          <div className="d-flex justify-content-center text-center team-header mb-4">
            <div className="d-flex justify-content-center align-items-center flex-column ms-auto">
              <h2 className="team-title mb-2 h1">Upcoming Events</h2>
            </div>
            <NavLink
              as={Link}
              to="/events"
              className="view-all-link ms-auto text-decoration-none"
            >
              View All
            </NavLink>
          </div>
          <span className="grad-1"></span>
          <span className="grad-2"></span>

          {loading ? (
            <div className="d-flex justify-content-center align-items-center">
              <Skeleton height={350} width={1300} borderRadius={12} />
            </div>
          ) : (
            <Carousel
              indicators={true}
              interval={5000}
              pause="hover"
              className="event-carousel"
              controls={false}
            >
              {displayEvents.map((event, idx) => {
                const startDate = parseDate(event?.start_date);
                const endDate = parseDate(event?.end_date);
                const showTimer = Boolean(startDate);
                const countdown = countdowns[event?._id || idx] || {
                  days: 0,
                  hours: 0,
                  mins: 0,
                  secs: 0,
                };

                // Date label rules:
                // - start only: show start
                // - start == end: show start only
                // - both present and different: show "start - end"
                // - no start: TBD
                let dateLabel;
                if (!startDate) {
                  dateLabel = "TBD";
                } else if (
                  endDate &&
                  startDate.toDateString() !== endDate.toDateString()
                ) {
                  dateLabel = `${formatToUSDate(
                    event?.start_date
                  )} - ${formatToUSDate(event?.end_date)}`;
                } else {
                  dateLabel = formatToUSDate(event?.start_date);
                }
                const venueLabel =
                  event?.venue && event.venue.trim() ? event.venue : "TBD";
                const hasPrice = event?.price && Number(event.price) > 0;

                const isMembershipValidForEvent = (expiryDate, eventDate) => {
                  if (!expiryDate || !eventDate) return false;

                  return (
                    new Date(expiryDate).getTime() >=
                    new Date(eventDate).getTime()
                  );
                };

                return (
                  <Carousel.Item key={event._id || idx}>
                    <Card className="p-4 radius-lg event-strip-card border-0 bg-transparent ">
                      <div className="d-flex flex-column flex-lg-row align-items-center gap-4">
                        <div className="event-thumb-image flex-shrink-0">
                          <img
                            src={
                              event?.image_path
                                ? `${IMAGE_BASEURL}${event?.image_path}`
                                : DefaultEventImage
                            }
                            alt={event?.title || "Event Image"}
                            className="event-thumb-img"
                          />
                        </div>
                        <div className="flex-grow-1 w-100">
                          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center gap-3">
                            <div className="event-info">
                              <h2 className="mb-3 event-title">
                                {event?.title}
                              </h2>
                              {hasPrice && (
                                <>
                                  {user &&
                                  user.is_membership_active &&
                                  isMembershipValidForEvent(
                                    user.membership_expiry_date,
                                    event?.start_date
                                  ) ? (
                                    <p className="mb-1 event-price">
                                      {" "}
                                      Free to{" "}
                                      {new Date(
                                        event?.start_date
                                      ).getFullYear()}{" "}
                                      Members
                                    </p>
                                  ) : (
                                    <p className="mb-3 event-price">
                                      Non-Members $
                                      {Number(event.price).toFixed(2)}
                                    </p>
                                  )}
                                </>
                              )}
                              <div className="d-flex flex-column align-items-start event-details-text">
                                <span className="d-flex align-items-center">
                                  <span className="bg-icons">
                                    <FaRegClock />
                                  </span>

                                  <span>{formatEventDate(event)}</span>
                                </span>

                                <span className="d-flex align-items-center">
                                  <span className="bg-icons">
                                    <HiOutlineLocationMarker />
                                  </span>
                                  <span>{venueLabel}</span>
                                </span>
                              </div>
                            </div>
                            {showTimer ? (
                              <div className="countdown-section d-none d-lg-flex flex-column align-items-center">
                                <div className="d-flex gap-0 gap-xl-2 align-items-center">
                                  <div className="count-box">
                                    <div className="count-value">
                                      {countdown?.days ?? 0}
                                    </div>
                                    <small className="count-label-white">
                                      DAYS
                                    </small>
                                  </div>
                                  <div className="count-box">
                                    <div className="count-value">
                                      {countdown?.hours ?? 0}
                                    </div>
                                    <small className="count-label-white">
                                      HOURS
                                    </small>
                                  </div>
                                  <div className="count-box">
                                    <div className="count-value">
                                      {countdown?.mins ?? 0}
                                    </div>
                                    <small className="count-label-white">
                                      MINS
                                    </small>
                                  </div>
                                  <div className="count-box">
                                    <div className="count-value">
                                      {countdown?.secs ?? 0}
                                    </div>
                                    <small className="count-label-white">
                                      SECS
                                    </small>
                                  </div>
                                </div>
                                <div className="mt-3 d-flex gap-2 flex-wrap">
                                  <Button
                                    className="btn btn-orange btn-event-details w-auto px-4 text-capitalize"
                                    onClick={() => {
                                      navigate(`/events/${event._id}`);
                                    }}
                                  >
                                    Purchase Tickets
                                  </Button>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Carousel.Item>
                );
              })}
            </Carousel>
          )}
        </section>
      )}
    </>
  );
}
